// src/features/my-shift/components/RequestForm.tsx
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import type { Shift } from "@/features/shift/types";
import { getShiftStartEnd, buildShiftRangeISO, isZeroLengthShift } from "@/features/shift/types";
import type { ShiftRequestCreateData } from "@/features/shift-request/types";

// M4: 接口改名为 RequestFormProps，与项目惯例一致
export interface RequestFormProps {
  shift: Shift;
  type: "leave" | "change";
  timezone: string;
  onSubmit: (data: ShiftRequestCreateData) => Promise<void>;
  onCancel: () => void;
}

/**
 * 从 ISO 时间字符串取本地 "HH:mm"（按时区）。
 *
 * M3: 使用 en-GB locale 是为了稳定输出 24h 格式（如 "14:30"），
 * 避免 en-US 在某些运行时偶发返回 "24:xx" 的边缘 bug。
 * 与 @/features/shift/types 的 toShiftTimeInputValue 同口径（那边 tz 可选）；formatShiftTime 按界面语言、只用于显示。
 * 此函数专为 <Input type="time"> 的 value 提供，必须严格 HH:mm。
 * off 班次无时间时返回 ""，不假填 "09:00" 误导用户。
 */
function toHHmm(iso: string, tz: string): string {
  // I3: 空 ISO → 返回 ""，不假填默认时间（off 班次场景）
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: tz });
}

// M4: 使用 RequestFormProps
export function RequestForm({ shift, type, timezone, onSubmit, onCancel }: RequestFormProps) {
  const { t } = useTranslation('shifts')
  const { startAt, endAt } = getShiftStartEnd(shift);
  const date = shift.scheduleDate || shift.schedule_date || "";
  const origStart = useMemo(() => toHHmm(startAt, timezone), [startAt, timezone]);
  const origEnd = useMemo(() => toHHmm(endAt, timezone), [endAt, timezone]);

  const [reason, setReason] = useState("");
  const [start, setStart] = useState(origStart);
  const [end, setEnd] = useState(origEnd);
  const [submitting, setSubmitting] = useState(false);

  // I1: shift 切换时同步重置 start/end/reason，防止旧值残留 + 假高亮
  useEffect(() => {
    setStart(origStart);
    setEnd(origEnd);
    setReason("");
  }, [shift.id, origStart, origEnd]);

  const startChanged = start !== origStart;
  const endChanged = end !== origEnd;

  const handleSubmit = async () => {
    if (!reason.trim()) {
      toast.error(t('myShift.requestForm.validation.provideReason'));
      return;
    }

    // I2: scheduleDate 为空时不提交，避免 buildISOWithTimezone 抛 Invalid time value
    if (type === "change" && !date) {
      toast.error(t('myShift.requestForm.validation.noDate'));
      return;
    }

    // I3: off 班次无时间，不能提交调班请求
    if (type === "change" && (!start || !end)) {
      toast.error(t('myShift.requestForm.validation.noTime'));
      return;
    }
    // 起止相等 = 0 时长(后端 end <= start 会 422);结束 < 开始才是跨午夜
    if (type === "change" && isZeroLengthShift(start, end)) {
      toast.error(t('shift.timeValidation.endNotAfterStart'));
      return;
    }

    // C2: 夜班结束在次日——按员工填的开始/结束时刻判断（结束 < 开始 = 跨午夜；相等已在上面拦下），
    // 不再沿用原排班 endAt 的日期：原排班本身就可能是「结束早于开始」的坏数据。
    // leave 不带时间,不拼 range(off / 无日期的班次拼出来是非法时刻)
    const range = type === "change" ? buildShiftRangeISO(date, start, end, timezone) : null;

    const data: ShiftRequestCreateData =
      type === "leave"
        ? { schedule_id: shift.id, request_type: "leave", reason: reason.trim() }
        : {
            schedule_id: shift.id,
            request_type: "change",
            reason: reason.trim(),
            payload: {
              proposed_start_at: range!.startAt,
              proposed_end_at: range!.endAt,
            },
          };
    setSubmitting(true);
    try {
      await onSubmit(data);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3">
      {type === "change" && (
        <div>
          <label className="block text-xs font-semibold text-muted-foreground mb-1">
            {t('myShift.requestForm.proposedNewTime')}
            {(startChanged || endChanged) && (
              <span className="ml-1.5 text-[10px] font-bold text-amber-600">{t('myShift.requestForm.changed')}</span>
            )}
          </label>
          <div className="flex gap-2">
            {/* I4: 用 <label htmlFor> + <Input id> 关联，提升可访问性 */}
            <div className="flex-1">
              <label htmlFor="req-start" className="block text-[10px] text-muted-foreground mb-1">{t('myShift.requestForm.start')}</label>
              <Input
                id="req-start"
                type="time" value={start} onChange={(e) => setStart(e.target.value)}
                className={startChanged ? "border-amber-300 bg-amber-50 dark:bg-amber-950/40 text-amber-800 font-semibold" : ""}
              />
            </div>
            <div className="flex-1">
              <label htmlFor="req-end" className="block text-[10px] text-muted-foreground mb-1">{t('myShift.requestForm.end')}</label>
              <Input
                id="req-end"
                type="time" value={end} onChange={(e) => setEnd(e.target.value)}
                className={endChanged ? "border-amber-300 bg-amber-50 dark:bg-amber-950/40 text-amber-800 font-semibold" : ""}
              />
            </div>
          </div>
        </div>
      )}

      <div>
        <label className="block text-xs font-semibold text-muted-foreground mb-1">{t('myShift.requestForm.reason')}</label>
        <Textarea
          placeholder={type === "leave" ? t('myShift.requestForm.leavePlaceholder') : t('myShift.requestForm.changePlaceholder')}
          value={reason} onChange={(e) => setReason(e.target.value)} rows={3} className="resize-none"
        />
      </div>

      <div className="flex gap-2 pt-1">
        <Button variant="outline" className="flex-1" onClick={onCancel} disabled={submitting}>
          {t('myShift.requestForm.cancel')}
        </Button>
        <Button className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground" onClick={handleSubmit} disabled={submitting}>
          {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {type === "leave" ? t('myShift.requestForm.submitLeave') : t('myShift.requestForm.submitChange')}
        </Button>
      </div>
    </div>
  );
}
